<template>
  <section class="max-w-7xl mx-auto pt-24">
    <div class="carousel-wrapper">
      <div class="carousel-container">
        <div
          v-for="(item, pos) in visibleSlides"
          :key="item?.id"
          class="carousel-item"
          :class="{
            'far-left': screenSize === 'large' && pos === 0,
            left:
              (screenSize === 'large' && pos === 1) ||
              (screenSize !== 'large' && pos === 0),
            active:
              (screenSize === 'large' && pos === 2) ||
              (screenSize !== 'large' && pos === 1),
            right:
              (screenSize === 'large' && pos === 3) ||
              (screenSize !== 'large' && pos === 2),
            'far-right': screenSize === 'large' && pos === 4,
            'far-right-enter':
              screenSize === 'large' && pos === 4 && isTransitioning,
          }"
        >
          <div class="carousel-content" @click="console.log(item?.id)">
            <NuxtImg
              :src="item?.image"
              :alt="item?.name || 'Plat best-seller'"
              width="234"
              height="234"
              loading="lazy"
              decoding="async"
              class="rounded-lg mx-auto pt-5"
            />
            <div class="absolute inset-0 flex flex-col justify-between p-4">
              <span
                class="self-end font-semibold text-2xl border rounded-full px-5 bg-transparent w-fit"
                style="color: var(--main-color)"
                >{{ item?.price }} €</span
              >
              <div class="self-start flex flex-col gap-1 items-start">
                <span
                  class="text-2xl text-black font-semibold rounded-full bg-transparent dark:text-white"
                  >{{ item?.name }}</span
                >
                <StarRating
                  v-if="item && item.numberOfRatings > 0"
                  :value="item.totalRatingsValue / item.numberOfRatings"
                  class="small-stars"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="carousel-controls-row">
        <button class="carousel-nav carousel-nav-prev" aria-label="Slide précédent" @click="prevSlide">
          <UIcon name="i-heroicons-chevron-left" class="text-2xl" />
        </button>
        <div class="carousel-pagination">
          <button
            v-for="(_, index) in slides"
            :key="index"
            class="carousel-dot"
            :class="{ active: currentIndex === index }"
            :aria-label="`Aller au slide ${index + 1}`"
            @click="goToSlide(index)"
          />
        </div>
        <button class="carousel-nav carousel-nav-next" aria-label="Slide suivant" @click="nextSlide">
          <UIcon name="i-heroicons-chevron-right" class="text-2xl" />
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import type { Rating, Repas } from "~/models/repas";
import StarRating from "~/components/star-rating.vue";

const { $api } = useNuxtApp();

const currentIndex = ref(0);
const autoplayInterval = ref<number | null>(null);
const screenSize = ref<"large" | "medium" | "small">("large");
const isTransitioning = ref(false);

function updateScreenSize() {
  const width = window.innerWidth;
  if (width < 640) {
    screenSize.value = "small"; // mobile
  } else if (width < 1024) {
    screenSize.value = "medium"; // tablet/small laptop
  } else {
    screenSize.value = "large"; // desktop
  }
}

onMounted(() => {
  updateScreenSize();
  window.addEventListener("resize", updateScreenSize);
  startAutoplay();
});

onUnmounted(() => {
  window.removeEventListener("resize", updateScreenSize);
  stopAutoplay();
});

const visibleSlides = computed(() => {
  if (!slides.value?.length) return [];
  const total = slides.value.length;
  if (screenSize.value === "large") {
    // 5 items
    const farLeft = (currentIndex.value - 2 + total) % total;
    const left = (currentIndex.value - 1 + total) % total;
    const center = currentIndex.value;
    const right = (currentIndex.value + 1) % total;
    const farRight = (currentIndex.value + 2) % total;
    return [
      slides.value[farLeft],
      slides.value[left],
      slides.value[center],
      slides.value[right],
      slides.value[farRight],
    ];
  } else {
    // 3 items
    const left = (currentIndex.value - 1 + total) % total;
    const center = currentIndex.value;
    const right = (currentIndex.value + 1) % total;

    return [slides.value[left], slides.value[center], slides.value[right]];
  }
});

function startAutoplay() {
  stopAutoplay();
  autoplayInterval.value = window.setInterval(() => {
    nextSlide();
  }, 4000);
}

function stopAutoplay() {
  if (autoplayInterval.value) {
    clearInterval(autoplayInterval.value);
    autoplayInterval.value = null;
  }
}

const nextSlide = () => {
  if (!slides.value?.length) return;
  isTransitioning.value = true;
  currentIndex.value = (currentIndex.value + 1) % slides.value.length;
  startAutoplay();
  setTimeout(() => {
    isTransitioning.value = false;
  }, 200); // Match the transition duration
};

const prevSlide = () => {
  if (!slides.value?.length) return;
  isTransitioning.value = true;
  currentIndex.value =
    (currentIndex.value - 1 + slides.value.length) % slides.value.length;
  startAutoplay();
  setTimeout(() => {
    isTransitioning.value = false;
  }, 200); // Match the transition duration
};

const goToSlide = (index: number) => {
  isTransitioning.value = true;
  currentIndex.value = index;
  startAutoplay();
  setTimeout(() => {
    isTransitioning.value = false;
  }, 200); // Match the transition duration
};

const { data: slides, refresh: refreshSlides } = await useAsyncData("slides", async () => {
  const resp: Repas[] = await $api.get("/repas/best-seller");
  return resp.map((item) => ({
    id: item.id,
    image: getImageUrl(item.image),
    price: item.price,
    name: item.name,
    totalRatingsValue: item.ratings?.reduce(
      (acc: number, rating: Rating) => acc + rating.value,
      0
    ) ?? 0,
    numberOfRatings: item.ratings?.length ?? 0,
  }));
});

onMounted(async () => {
  if (!slides.value || slides.value.length === 0) {
    await refreshSlides();
  }
});
</script>

<style scoped>
.carousel-wrapper {
  position: relative;
  width: 100%;
  padding: 4rem 0;
  perspective: 2000px;
  overflow: hidden;
}

.carousel-container {
  position: relative;
  width: 100%;
  height: 340px;
  display: flex;
  justify-content: center;
  align-items: center;
  transform-style: preserve-3d;
}

.carousel-item {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 290px;
  height: 320px;
  transform-origin: center center;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transform-style: preserve-3d;
  backface-visibility: hidden;
  will-change: transform, opacity;
}

.carousel-content {
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
}

.carousel-item.far-left {
  z-index: 10;
  transform: translate(-220%, -50%) scale(0.8) translateZ(-200px);
  opacity: 0.7;
}

.carousel-item.left {
  z-index: 20;
  transform: translate(-140%, -50%) scale(0.95) translateZ(-80px);
  opacity: 1;
}

.carousel-item.active {
  z-index: 40;
  transform: translate(-50%, -50%) scale(1.1) translateZ(0);
  opacity: 1;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.2);
}

.carousel-item.right {
  z-index: 20;
  transform: translate(40%, -50%) scale(0.95) translateZ(-80px);
  opacity: 1;
}

.carousel-item.far-right {
  z-index: 10;
  transform: translate(120%, -50%) scale(0.8) translateZ(-200px);
  opacity: 0.7;
}

.carousel-item.far-right-enter {
  transform: translate(300%, -50%) scale(0.6) translateZ(-300px);
  opacity: 0;
}

.carousel-item:not(.active):not(.left):not(.right):not(.far-left):not(
    .far-right
  ) {
  opacity: 0;
  pointer-events: none;
  z-index: 0;
  transform: translate(-300%, -50%) scale(0.6) translateZ(-300px);
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.carousel-controls-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.carousel-nav {
  position: static;
  top: unset;
  left: unset;
  right: unset;
  transform: none;
  z-index: 1;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: transparent;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.dark .carousel-nav {
  color: white;
}

.carousel-nav:hover {
  background-color: var(--main-color);
  color: white;
}

.carousel-nav-prev {
  left: 1rem;
}

.carousel-nav-next {
  right: 1rem;
}

.carousel-pagination {
  position: static;
  bottom: unset;
  left: unset;
  transform: none;
  display: flex;
  gap: 0;
  z-index: 1;
}

.carousel-dot {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 9999px;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-dot::before {
  content: "";
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background-color: rgb(209 213 219);
  transition: all 0.3s ease;
}

.carousel-dot.active::before {
  width: 1rem;
  background-color: var(--main-color);
}

/* Responsive transforms for carousel items */
@media (max-width: 1023px) and (min-width: 640px) {
  /* Tablet/small laptop: 3 items */
  .carousel-item.left {
    z-index: 10;
    transform: translate(-140%, -50%) scale(0.9) translateZ(-60px);
    opacity: 1;
  }
  .carousel-item.active {
    z-index: 20;
    transform: translate(-50%, -50%) scale(1.05) translateZ(0);
    opacity: 1;
  }
  .carousel-item.right {
    z-index: 10;
    transform: translate(50%, -50%) scale(0.9) translateZ(-60px);
    opacity: 1;
  }
  .carousel-item.far-left,
  .carousel-item.far-right {
    opacity: 0;
    pointer-events: none;
  }
}

@media (max-width: 639px) {
  /* Mobile: 1.5 items (centered, with peeking prev/next) */
  .carousel-wrapper {
    padding: 2rem 0;
    overflow: hidden;
  }

  .carousel-item {
    width: 85vw;
    max-width: 280px;
    transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .carousel-item.left {
    z-index: 10;
    transform: translate(-130%, -50%) scale(0.85) translateZ(-40px);
    opacity: 0.7;
  }

  .carousel-item.active {
    z-index: 20;
    transform: translate(-50%, -50%) scale(1) translateZ(0);
    opacity: 1;
  }

  .carousel-item.right {
    z-index: 10;
    transform: translate(30%, -50%) scale(0.85) translateZ(-40px);
    opacity: 0.7;
  }

  .carousel-item.far-left,
  .carousel-item.far-right {
    opacity: 0;
    pointer-events: none;
  }
}
</style>
